/**
* SchemaGenerator — pure, side-effect-free JSON-LD builders.
*
* Compose with `buildJsonLd([...])` to emit a single `@graph` per page.
* A graph lets nodes reference each other by `@id` (Organization, WebSite,
* WebPage, Article, Author) which validators score higher than N loose scripts.
*
* Undefined fields are stripped before serialization to guarantee zero errors
* on Rich Results / schema.org validator.
*/
import { SITE } from "./site-config";
import { compactJsonLd } from "./jsonld-compact";

type Json = string | number | boolean | null | Json[] | { [key: string]: Json | undefined };

function clean<T extends Json>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => clean(v as Json)).filter((v) => v !== undefined) as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, Json> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      const c = clean(v as Json);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return value;
}

/** Compose N schemas into a JSON string ready for `<script type="application/ld+json">`. */
export function buildJsonLd(schemas: Array<unknown>): string {
  const graph = schemas.filter(Boolean) as Json[];
  return JSON.stringify(
    compactJsonLd(
      clean({
        "@context": "https://schema.org",
        "@graph": graph,
      }),
    ) ?? {},
  );
}

/** Convenience wrapper for TanStack head().scripts. */
export function jsonLdScript(payload: string | Record<string, unknown>) {
  return {
    type: "application/ld+json",
    children:
      typeof payload === "string" ? payload : JSON.stringify(compactJsonLd(payload) ?? {}),
  };
}


// ── Core identity nodes ────────────────────────────────────────────────────

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": "https://thainotarylaw.com/#organization",
    name: SITE.legalName,
    alternateName: SITE.name,
    url: "/",
    logo: SITE.logoPath,
    description: SITE.description,
    foundingDate: String(SITE.founded),
    contactPoint: {
      "@type": "ContactPoint",
      email: SITE.contact.email,
      telephone: SITE.contact.phone,
      contactType: "customer support",
      availableLanguage: [SITE.language],
    },
    address: {
      "@type": "PostalAddress",
      ...SITE.contact.address,
    },
    sameAs: [
      SITE.social.linkedin,
      SITE.social.facebook,
      SITE.social.youtube,
      SITE.social.github,
      SITE.social.twitter ? `https://twitter.com/${SITE.social.twitter.replace(/^@/, "")}` : "",
    ].filter(Boolean),
  };
}

/** WebSite + SearchAction — enables Sitelinks Search Box in Google. */
export function webSiteSchema() {
  return {
    "@type": "WebSite",
    "@id": "https://thainotarylaw.com/#website",
    url: "/",
    name: SITE.name,
    publisher: { "@id": "https://thainotarylaw.com/#organization" },
    inLanguage: SITE.language,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function webPageSchema(input: {
  url: string;
  title: string;
  description: string;
  lastModified?: string;
  speakable?: boolean;
  inLanguage?: string;
}) {
  return {
    "@type": "WebPage",
    "@id": `${input.url}#webpage`,
    url: input.url,
    name: input.title,
    description: input.description,
    isPartOf: { "@id": "https://thainotarylaw.com/#website" },
    inLanguage: input.inLanguage ?? SITE.language,
    dateModified: input.lastModified,
    // Speakable is on by default: the configured selectors (h1, .answer-summary,
    // .speakable-summary, .lead) exist on every page through PageHero, so AI
    // answer engines and voice surfaces always have an extractable answer.
    speakable:
      input.speakable === false
        ? undefined
        : {
            "@type": "SpeakableSpecification",
            cssSelector: SITE.aeo.speakableSelectors,
          },

  };
}


// ── Navigation aids ────────────────────────────────────────────────────────

export function breadcrumbListSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

// ── Content types ──────────────────────────────────────────────────────────

export function faqPageSchema(faqs: Array<{ question: string; answer: string }>) {
  if (!faqs.length) return undefined as unknown as Record<string, unknown>;
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export function articleSchema(input: {
  url: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  authorName?: string;
  authorUrl?: string;
  wordCount?: number;
  articleSection?: string;
  keywords?: string[];
  type?: "Article" | "BlogPosting" | "NewsArticle";
}) {
  return {
    "@type": input.type ?? "Article",
    "@id": `${input.url}#article`,
    mainEntityOfPage: { "@id": `${input.url}#webpage` },
    headline: input.headline,
    description: input.description,
    image: input.image,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    wordCount: input.wordCount,
    articleSection: input.articleSection,
    keywords: input.keywords?.join(", "),
    author: input.authorName
      ? {
          "@type": "Person",
          name: input.authorName,
          url: input.authorUrl,
        }
      : { "@id": "https://thainotarylaw.com/#organization" },
    publisher: { "@id": "https://thainotarylaw.com/#organization" },
    inLanguage: SITE.language,
  };
}

export function legalServiceSchema(input: {
  name: string;
  description: string;
  url: string;
  areaServed?: string;
  serviceType?: string;
  /** Optional nested OfferCatalog for richer Service entity graphs. */
  hasOfferCatalog?: ReturnType<typeof offerCatalogSchema>;
}) {
  return {
    "@type": "LegalService",
    "@id": `${input.url}#service`,
    name: input.name,
    description: input.description,
    url: input.url,
    provider: { "@id": "https://thainotarylaw.com/#organization" },
    areaServed: input.areaServed,
    serviceType: input.serviceType,
    telephone: SITE.contact.phone,
    address: {
      "@type": "PostalAddress",
      ...SITE.contact.address,
    },
    hasOfferCatalog: input.hasOfferCatalog,
  };
}

export function personSchema(input: {
  name: string;
  url: string;
  jobTitle?: string;
  description?: string;
  image?: string;
  credentials?: string | string[];
  email?: string;
  sameAs?: string[];
}) {
  return {
    "@type": "Person",
    "@id": `${input.url}#person`,
    name: input.name,
    url: input.url,
    jobTitle: input.jobTitle,
    description: input.description,
    image: input.image,
    hasCredential: Array.isArray(input.credentials)
      ? input.credentials
      : input.credentials,
    email: input.email,
    sameAs: input.sameAs?.filter(Boolean),
    worksFor: { "@id": "https://thainotarylaw.com/#organization" },
  };
}

// ── Rich content types (opt-in per page) ──────────────────────────────────

export function howToSchema(input: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string; url?: string; image?: string }>;
  totalTime?: string; // ISO 8601 duration, e.g. "PT30M"
  estimatedCost?: { currency: string; value: string };
}) {
  return {
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    totalTime: input.totalTime,
    estimatedCost: input.estimatedCost
      ? {
          "@type": "MonetaryAmount",
          currency: input.estimatedCost.currency,
          value: input.estimatedCost.value,
        }
      : undefined,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: s.url,
      image: s.image,
    })),
  };
}

export function productSchema(input: {
  url: string;
  name: string;
  description: string;
  image?: string | string[];
  brand?: string;
  sku?: string;
  offer?: {
    price: string;
    currency: string;
    availability?: "InStock" | "OutOfStock" | "PreOrder";
    url?: string;
  };
  aggregateRating?: { ratingValue: number; reviewCount: number };
}) {
  return {
    "@type": "Product",
    "@id": `${input.url}#product`,
    name: input.name,
    description: input.description,
    image: input.image,
    brand: input.brand ? { "@type": "Brand", name: input.brand } : undefined,
    sku: input.sku,
    offers: input.offer
      ? {
          "@type": "Offer",
          availability: `https://schema.org/${input.offer.availability ?? "InStock"}`,
          url: input.offer.url ?? input.url,
        }
      : undefined,
    aggregateRating: input.aggregateRating
      ? {
          "@type": "AggregateRating",
          ratingValue: input.aggregateRating.ratingValue,
          reviewCount: input.aggregateRating.reviewCount,
        }
      : undefined,
  };
}

export function videoObjectSchema(input: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  contentUrl?: string;
  embedUrl?: string;
  duration?: string; // ISO 8601 duration
}) {
  return {
    "@type": "VideoObject",
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnailUrl,
    uploadDate: input.uploadDate,
    contentUrl: input.contentUrl,
    embedUrl: input.embedUrl,
    duration: input.duration,
  };
}

/** Hreflang helper — returns an array ready for `head().links`. */
export function hreflangLinks(
  path: string,
  alternates: Array<{ lang: string; href: string }>,
) {
  return [
    ...alternates.map((a) => ({
      rel: "alternate",
      hrefLang: a.lang,
      href: a.href,
    })),
    { rel: "alternate", hrefLang: "x-default", href: path },
  ];
}

// ── Thai Notary Law — master entity graph helpers ─────────────────────────

export function attorneySchema(input: {
  url: string; name: string; role: string; image?: string;
  languages?: string[]; specialties?: string[];
}) {
  return {
    "@type": ["Person", "Attorney"],
    "@id": `${input.url}#person`,
    name: input.name,
    jobTitle: input.role,
    image: input.image,
    knowsLanguage: input.languages,
    knowsAbout: input.specialties,
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "license",
      name: "Notarial Services Attorney License",
      recognizedBy: {
        "@type": "Organization",
        name: "Lawyers Council of Thailand",
        url: "https://www.lawyerscouncil.or.th",
      },
    },
    worksFor: { "@id": "https://thainotarylaw.com/#organization" },
  };
}

export function localBusinessSchema(input: {
  id: string; name: string; street: string; locality: string;
  region: string; postalCode: string; country: string; phone: string;
  lat: number; lng: number; mapUrl?: string;
}) {
  return {
    "@type": ["LocalBusiness", "LegalService"],
    "@id": `#branch-${input.id}`,
    name: input.name,
    telephone: input.phone,
    ...(input.mapUrl ? { hasMap: input.mapUrl } : {}),
    parentOrganization: { "@id": "https://thainotarylaw.com/#organization" },
    address: {
      "@type": "PostalAddress",
      streetAddress: input.street,
      addressLocality: input.locality,
      addressRegion: input.region,
      postalCode: input.postalCode,
      addressCountry: input.country,
    },
    geo: { "@type": "GeoCoordinates", latitude: input.lat, longitude: input.lng },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "09:00", closes: "18:00",
    },
  };
}

export function offerCatalogSchema(input: {
  categoryUrl: string; categoryName: string;
  items: Array<{ slug?: string; name: string; price?: number; unit?: string; summary?: string }>;
}) {
  return {
    "@type": "OfferCatalog",
    "@id": `${input.categoryUrl}#catalog`,
    name: input.categoryName,
    itemListElement: input.items.map((it, idx) => {
      const anchor = it.slug ? `${input.categoryUrl}#${it.slug}` : `${input.categoryUrl}#item-${idx + 1}`;
      return {
        "@type": "Offer",
        url: anchor,
        // No published price: the firm quotes per case, so emitting a price
        // here would contradict the page (Google structured-data mismatch).
        availability: "https://schema.org/InStock",
        // Minimal Service node: name + url + a short description is everything
        // Rich Results reads here. Provider/areaServed/serviceType are inherited
        // from the parent LegalService node, so repeating them per offer only
        // inflated the payload (they were pure duplication).
        itemOffered: {
          "@type": "Service",
          name: it.name,
          description: (it.summary ?? it.name).replace(/\s+/g, " ").slice(0, 160),
          url: anchor,
        },
      };
    }),
  };
}


/** ItemList — helps Google render list rich results for sub-service tables. */
export function itemListSchema(input: {
  url: string;
  name: string;
  items: Array<{ slug?: string; name: string }>;
}) {
  return {
    "@type": "ItemList",
    "@id": `${input.url}#itemlist`,
    name: input.name,
    numberOfItems: input.items.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: input.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.slug ? `${input.url}#${it.slug}` : `${input.url}#item-${i + 1}`,
    })),
  };
}

export function masterEntityNodes(input: {
  branches: Array<{
    id: string; name: string; street: string; locality: string;
    region: string; postalCode: string; country: string; phone: string;
    geo: { lat: number; lng: number };
  }>;
  attorneys: Array<{
    slug: string; name: string; role: string; image?: string;
    languages?: string[]; specialties?: string[];
  }>;
}) {
  const branches = input.branches.map((b) =>
    localBusinessSchema({
      id: b.id, name: b.name, street: b.street, locality: b.locality,
      region: b.region, postalCode: b.postalCode, country: b.country,
      phone: b.phone, lat: b.geo.lat, lng: b.geo.lng,
    }),
  );
  const attorneys = input.attorneys.map((a) =>
    attorneySchema({
      url: `/about#${a.slug}`, name: a.name, role: a.role, image: a.image,
      languages: a.languages, specialties: a.specialties,
    }),
  );
  return [organizationSchema(), webSiteSchema(), ...branches, ...attorneys];
}

